// La visionneuse de statuts — plein écran, comme sur Facebook et Instagram.
//
// Elle s'ouvre depuis deux endroits : le bandeau du fil, et la photo de profil
// d'un membre qui a un statut en cours. Le second appelant n'est pas toujours un
// composant React, et la visionneuse n'appartient à aucun écran : elle se monte
// donc SUR LE CORPS DE LA PAGE, avec sa propre racine React — le même geste que
// `pont.jsx` fait pour les écrans.
//
// Trois comportements qu'il ne faut pas perdre :
//   1. une photo ou un texte défile tout seul au bout de 6 s ; une vidéo attend
//      sa fin — sinon on couperait la parole à celui qui parle ;
//   2. écrire une réponse SUSPEND le défilement — rien de plus agaçant que de
//      voir le statut changer pendant qu'on tape ;
//   3. une vue est comptée quand le statut s'affiche vraiment, pas au survol du
//      bandeau, et jamais sur ses propres statuts.

import { createRoot } from "react-dom/client";
import { StrictMode, useEffect, useRef, useState } from "react";
import Store, { lireUneFois } from "../store.js";
import { APP } from "../config.js";
import { ilYA, toast, confirmer, modale, esc, avatar } from "../ui.js";
import Avatar from "./Avatar.jsx";
import Certifie from "./Certifie.jsx";
import Icone from "./Icone.jsx";
import { useMembre } from "./hooks.js";
import { REACTIONS } from "../reactions.js";
import { Pastille, Resume, resumer } from "./Reactions.jsx";

const FONDS = APP.fondsStory;
const DUREE = 6000;

/** Qui a vu ce statut. Réservé à son auteur, comme sur Facebook. */
async function montrerVues(s) {
  const { el } = modale({
    titre: "Qui a vu ce statut",
    corps: `<p class="texte-doux petit">Un instant…</p>`,
  });
  const corps = el.querySelector(".modal__corps");
  try {
    const gens = await Store.vues.liste("stories", s.id);
    corps.innerHTML = gens.length
      ? `<div class="grille-membres">${gens
          .map(
            (v) => `
        <a class="membre" href="#/profil/${esc(v.uid)}">
          <span class="membre__qui">
            ${avatar({ nom: v.nom, photo: v.photo }, 44)}
            <span><strong>${esc(v.nom || "Membre")}</strong>
            <span class="petit texte-doux">${esc(ilYA(v.le))}</span></span>
          </span>
        </a>`
          )
          .join("")}</div>`
      : `<p class="texte-doux petit">Personne ne l'a encore ouvert.</p>`;
  } catch (e) {
    corps.innerHTML = `<p class="texte-doux petit">${esc(e.message || "Liste indisponible")}</p>`;
  }
}

/**
 * Répondre à un statut.
 *
 * La réponse part en message privé, comme sur Facebook et Instagram : un statut
 * est éphémère, une réponse publique n'aurait bientôt plus de contexte. On
 * rappelle donc le statut en tête du message, sans quoi l'auteur recevrait une
 * phrase sans savoir à quoi elle répond.
 */
async function repondreAuStatut(s, texte) {
  /* ⚠️ LE STATUT VOYAGE AVEC LA RÉPONSE, il n'est plus recopié dans la phrase.
     Avant, le message partait ainsi : « ↩ En réponse à votre statut en image »
     suivi du texte. Pour un statut ÉCRIT, cela passait encore. Pour une photo
     ou une vidéo, l'auteur recevait une phrase qui ne montrait rien : il en
     publie plusieurs par jour et n'avait aucun moyen de savoir laquelle. La
     réponse arrivait donc sans ce à quoi elle répond.
     Désormais la vignette part avec le message et la messagerie la redessine
     au-dessus de la bulle — comme Messenger. Le texte, lui, reste ce que la
     personne a réellement écrit, et rien d'autre. */
  const apercu = s.texte
    ? s.texte.slice(0, 120)
    : s.type === "video"
    ? "Statut vidéo"
    : "Statut en image";
  try {
    const cid = await Store.convos.ensure(s.authorId);
    // Pas de notification : le message se signale lui-même par la pastille de
    // la messagerie (voir majBadgeMessages dans app.js).
    await Store.messages.send(cid, texte, {
      repondAStatut: {
        id: s.id,
        type: s.type || "texte",
        image: s.type === "image" ? s.media || "" : "",
        apercu,
        fond: s.type === "texte" ? FONDS[s.fond || 0] || "" : "",
      },
    });
    toast("Réponse envoyée 🙌");
  } catch (e) {
    toast(e.message || "Réponse impossible", "erreur");
  }
}

function Visionneuse({ liste, estMoi, fermer, ctx }) {
  const [i, setI] = useState(0);
  const [reponse, setReponse] = useState("");
  const [suspendu, setSuspendu] = useState(false);
  const s = liste[Math.min(i, liste.length - 1)];
  const auteur = useMembre(ctx, s.authorId, { nom: s.auteurNom, photo: s.auteurPhoto });

  const suivant = () => setI((n) => (n < liste.length - 1 ? n + 1 : (fermer(), n)));
  const precedent = () => setI((n) => Math.max(0, n - 1));

  // Le clavier : Échap ferme, les flèches parcourent.
  useEffect(() => {
    const clavier = (e) => {
      if (e.key === "Escape") fermer();
      if (e.key === "ArrowRight") suivant();
      if (e.key === "ArrowLeft") precedent();
    };
    document.addEventListener("keydown", clavier);
    document.body.classList.add("no-scroll");
    return () => {
      document.removeEventListener("keydown", clavier);
      document.body.classList.remove("no-scroll");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liste.length]);

  // Le défilement automatique — sauf pour une vidéo, qui se termine d'elle-même,
  // et sauf pendant qu'on écrit une réponse.
  useEffect(() => {
    if (suspendu || s.type === "video") return;
    const t = setTimeout(suivant, DUREE);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, suspendu, s.type]);

  // La vue, comptée à l'affichage réel. Une seule écriture par membre et par
  // statut : le pilote s'arrête de lui-même si la vue existe déjà.
  useEffect(() => {
    if (!estMoi) Store.vues?.marquer("stories", s.id).catch(() => {});
  }, [s.id, estMoi]);

  /* Réagir SUSPEND le défilement, pour la même raison qu'écrire une réponse :
     choisir sa réaction et voir le statut changer sous le doigt, c'est réagir
     au suivant sans l'avoir voulu. */
  /* ⚠️ L'INSTANTANÉ NE BOUGE PAS — 20 octobre 2026, trouvé à l'audit. La
     visionneuse est rendue sur sa propre racine, à partir d'une photographie
     de la liste : après `reagir`, `s.reactions` ne change jamais. La réaction
     posée ne s'allumait donc jamais — et la retoucher la RETIRAIT côté
     serveur pendant que l'écran, croyant qu'on n'avait rien posé, envoyait
     une alerte à l'auteur… pour un retrait. On tient donc SA réaction
     localement, statut par statut : l'instantané donne le point de départ,
     chaque geste le corrige. */
  const resumeReactions = resumer(s.reactions, ctx.moi?.uid);
  const [miennes, setMiennes] = useState({});
  const mienne = s.id in miennes ? miennes[s.id] : resumeReactions.mienne;
  const reagir = async (cle) => {
    setSuspendu(true);
    const voulue = mienne === cle ? null : cle; // reposer la même la retire
    try {
      await Store.stories.reagir(s.id, cle);
      setMiennes((m) => ({ ...m, [s.id]: voulue }));
      // Prévenu d'un AJOUT seulement — jamais d'un retrait ni d'un changement d'avis.
      if (voulue && !mienne)
        Store.notifs.add(s.authorId, {
          type: "story",
          deId: ctx.moi.uid,
          deNom: ctx.moi.nom,
          dePhoto: ctx.moi.photo || "",
        });
    } catch (e) {
      toast(e.message || "Réaction impossible", "erreur");
    }
  };

  const supprimer = async () => {
    setSuspendu(true);
    if (await confirmer("Supprimer ce statut ?", "Il ne sera plus visible par personne.", "Supprimer")) {
      await Store.stories.remove(s.id);
      toast("Statut supprimé");
      return fermer();
    }
    setSuspendu(false);
  };

  const envoyer = async (e) => {
    e.preventDefault();
    const t = reponse.trim();
    if (!t) return;
    setReponse("");
    await repondreAuStatut(s, t);
    setSuspendu(false);
  };

  return (
    <div className="visionneuse" onClick={(e) => e.target === e.currentTarget && fermer()}>
      <div className="visionneuse__boite">
        <div className="visionneuse__barres">
          {liste.map((_, k) => (
            <span key={k} className={k < i ? "est-vue" : k === i ? "est-active" : ""} />
          ))}
        </div>

        <header className="visionneuse__tete">
          <Avatar membre={auteur} taille={38} />
          <span>
            <strong>
              {auteur.nom}
              <Certifie membre={auteur} taille={13} />
            </strong>
            <span className="petit">{ilYA(s.createdAt)}</span>
          </span>
          {estMoi && (
            <button className="icon-btn" aria-label="Supprimer" onClick={supprimer}>
              <Icone nom="fermer" taille={18} />
            </button>
          )}
          <button className="icon-btn" aria-label="Fermer" onClick={fermer}>
            <Icone nom="fermer" taille={18} />
          </button>
        </header>

        <div
          className="visionneuse__contenu"
          style={s.type === "texte" ? { background: FONDS[s.fond || 0] } : undefined}
        >
          {s.type === "image" && <img src={s.media} alt="" />}
          {s.type === "video" && (
            <video src={s.media} autoPlay playsInline controls onEnded={suivant} key={s.id} />
          )}
          {s.texte && (
            <p className={`visionneuse__texte${s.type === "texte" ? " visionneuse__texte--grand" : ""}`}>
              {s.texte}
            </p>
          )}
        </div>

        <button className="visionneuse__zone visionneuse__zone--g" aria-label="Précédent" onClick={precedent} />
        <button className="visionneuse__zone visionneuse__zone--d" aria-label="Suivant" onClick={suivant} />

        {estMoi ? (
          <div className="visionneuse__bas">
            <button
              className="visionneuse__vues"
              onClick={() => {
                setSuspendu(true); // on ne fait pas défiler pendant qu'il regarde
                montrerVues(s);
              }}
            >
              <Icone nom="oeil" taille={18} /> <span>{s.vues || 0}</span>
            </button>
            {/* Sur SON propre statut, l'auteur ne réagit pas : il regarde ce
                qu'on lui a répondu. */}
            <Resume total={resumeReactions.total} tetes={resumeReactions.tetes} className="reaction-resume--story" />
          </div>
        ) : (
          <div className="visionneuse__bas">
            {/* La palette, toujours ouverte : un statut dure vingt-quatre heures
                et se regarde d'un doigt. Cacher les réactions derrière un appui
                long, ici, ce serait les cacher tout court. */}
            <div className="visionneuse__reactions">
              {REACTIONS.map((r) => (
                <button
                  key={r.cle}
                  type="button"
                  className={`visionneuse__reaction${mienne === r.cle ? " est-actif" : ""}`}
                  aria-label={r.label}
                  onClick={() => reagir(r.cle)}
                >
                  <Pastille cle={r.cle} taille={30} titre={r.label} />
                </button>
              ))}
            </div>
            <form className="visionneuse__reponse" onSubmit={envoyer}>
              <input
                type="text"
                maxLength={500}
                value={reponse}
                placeholder={`Répondre à ${(s.auteurNom || "").split(" ")[0] || "ce statut"}…`}
                aria-label="Répondre au statut"
                onFocus={() => setSuspendu(true)}
                onChange={(e) => setReponse(e.target.value)}
              />
              <button className="icon-btn icon-btn--envoi" type="submit" aria-label="Envoyer">
                <Icone nom="envoyer" taille={17} />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- ouverture impérative */

/** Monte la visionneuse sur le corps de la page, avec sa propre racine React. */
export function ouvrirVisionneuse(toutes, auteurId, ctx, estMoi = false) {
  const liste = (toutes || []).filter((s) => s.authorId === auteurId);
  if (!liste.length) return;

  const hote = document.createElement("div");
  document.body.appendChild(hote);
  const racine = createRoot(hote);

  const fermer = () => {
    hote.remove();
    // différé : React interdit de démonter pendant qu'il rend, et la fermeture
    // part justement d'un clic à l'intérieur du composant
    setTimeout(() => racine.unmount(), 0);
  };

  racine.render(
    <StrictMode>
      <Visionneuse liste={liste} estMoi={estMoi} fermer={fermer} ctx={ctx} />
    </StrictMode>
  );
}

/**
 * Ouvre les statuts d'un membre depuis n'importe où — une photo de profil, par
 * exemple. On va chercher la liste courante, puis on lance la visionneuse.
 */
export async function ouvrirStatutsDe(auteurId, ctx) {
  const toutes = await lireUneFois((cb) => Store.stories.subscribe(cb), []);
  if (!(toutes || []).some((s) => s.authorId === auteurId))
    return toast("Ce membre n'a plus de statut en cours");
  ouvrirVisionneuse(toutes, auteurId, ctx, auteurId === ctx.moi.uid);
}
